import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, Shield, MapPin, ChevronLeft, CheckCircle, User } from 'lucide-react';
import { useCustomer } from '../../context/CustomerContext';
import FflDealerModal from '../../components/FflDealerModal';

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function AccountProfile() {
  const { customer, loading: customerLoading, token, login } = useCustomer();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '',
    address: '', city: '', state: '', zip: '',
  });
  const [fflDealer, setFflDealer] = useState(null);
  const [fflModalOpen, setFflModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!customer) return;
    setForm({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      phone: customer.phone || '',
      address: customer.shipping_address?.address || '',
      city: customer.shipping_address?.city || '',
      state: customer.shipping_address?.state || '',
      zip: customer.shipping_address?.zip || '',
    });
    setFflDealer(customer.preferred_ffl_dealer || null);
  }, [customer]);

  if (customerLoading) return null;
  if (!customer) { navigate('/account/login'); return null; }

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess(false);
    try {
      const res = await fetch(`${BASE}/api/customer/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          shipping_address: { address: form.address, city: form.city, state: form.state, zip: form.zip },
          preferred_ffl_dealer: fflDealer || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      login(token, data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const inputCls = "w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600";
  const labelCls = "block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/account" className="text-zinc-500 hover:text-white transition-colors">
            <ChevronLeft size={20} />
          </Link>
          <div>
            <h1 className="font-bebas text-3xl text-white tracking-wider">ACCOUNT SETTINGS</h1>
            <p className="font-inter text-xs text-zinc-500">{customer.email}</p>
          </div>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-5">{error}</div>}
        {success && <div className="bg-green-500/10 border border-green-500/30 text-green-400 font-inter text-sm px-4 py-3 mb-5 flex items-center gap-2"><CheckCircle size={14} />Profile saved successfully</div>}

        <form onSubmit={handleSave} className="space-y-6">
          {/* Personal Info */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h3 className="font-bebas text-lg text-white tracking-wider mb-4 flex items-center gap-2">
              <User size={16} className="text-orange-500" />PERSONAL INFORMATION
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className={labelCls}>First Name</label><input value={form.first_name} onChange={set('first_name')} className={inputCls} placeholder="John" /></div>
              <div><label className={labelCls}>Last Name</label><input value={form.last_name} onChange={set('last_name')} className={inputCls} placeholder="Doe" /></div>
            </div>
            <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={set('phone')} className={inputCls} placeholder="(555) 555-5555" /></div>
          </div>

          {/* Shipping Address */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <h3 className="font-bebas text-lg text-white tracking-wider mb-4 flex items-center gap-2">
              <MapPin size={16} className="text-orange-500" />DEFAULT SHIPPING ADDRESS
            </h3>
            <div className="space-y-3">
              <div><label className={labelCls}>Street Address</label><input value={form.address} onChange={set('address')} className={inputCls} placeholder="123 Main St" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1"><label className={labelCls}>City</label><input value={form.city} onChange={set('city')} className={inputCls} placeholder="Austin" /></div>
                <div>
                  <label className={labelCls}>State</label>
                  <select value={form.state} onChange={set('state')} className={inputCls}>
                    <option value="">—</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Zip</label><input value={form.zip} onChange={set('zip')} className={inputCls} placeholder="78701" /></div>
              </div>
            </div>
          </div>

          {/* FFL Dealer Preference */}
          <div className="bg-[#1a1a1a] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bebas text-lg text-white tracking-wider flex items-center gap-2">
                <Shield size={16} className="text-orange-500" />PREFERRED FFL DEALER
              </h3>
              <button type="button" onClick={() => setFflModalOpen(true)}
                className="font-inter text-xs text-orange-400 hover:text-orange-300 border border-orange-500/30 hover:border-orange-500 px-3 py-1.5 transition-colors">
                {fflDealer?.business_name ? 'Change Dealer' : 'Search & Select →'}
              </button>
            </div>

            {fflDealer?.business_name ? (
              <div className="flex items-start gap-3 bg-orange-500/5 border border-orange-500/20 p-3">
                <Shield size={16} className="text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-inter text-sm text-white font-semibold">{fflDealer.business_name}</p>
                  <p className="font-inter text-xs text-zinc-400">
                    {[fflDealer.address, fflDealer.city, fflDealer.state, fflDealer.zip].filter(Boolean).join(', ')}
                  </p>
                  {fflDealer.phone && <p className="font-inter text-xs text-zinc-500">{fflDealer.phone}</p>}
                  {fflDealer.license_number && <p className="font-inter text-[10px] text-zinc-600 mt-0.5">FFL: {fflDealer.license_number}</p>}
                  <button type="button" onClick={() => setFflDealer(null)}
                    className="font-inter text-[10px] text-zinc-500 hover:text-red-400 mt-1 transition-colors">
                    Remove dealer
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 border border-dashed border-white/10">
                <Shield size={24} className="text-zinc-700 mx-auto mb-2" />
                <p className="font-inter text-sm text-zinc-500 mb-3">No FFL dealer saved yet</p>
                <p className="font-inter text-xs text-zinc-600">
                  Save your local FFL/SOT dealer so it pre-fills at checkout when buying suppressors.
                </p>
              </div>
            )}
          </div>

          <button type="submit" disabled={saving}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-xl tracking-widest py-4 transition-colors">
            {saving ? 'SAVING...' : 'SAVE PROFILE'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/account" className="font-inter text-xs text-zinc-600 hover:text-zinc-400 transition-colors">← Back to dashboard</Link>
        </div>
      </div>

      <FflDealerModal
        isOpen={fflModalOpen}
        onClose={() => setFflModalOpen(false)}
        onSelect={(dealer) => { setFflDealer(dealer); setFflModalOpen(false); }}
        selectedDealer={fflDealer}
      />
    </div>
  );
}
